import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useArc, CORE_STREAK_PILLARS, CORE_STREAK_OPTION_GROUPS, getCoreStreakStatus } from '../context/ArcContext';
import { PILLARS } from '../mock/mock';
import { localDateKey } from '../lib/dates';
import { useLocalDateKey } from '../hooks/useLocalDateKey';
import {
  Droplets, Flame, Activity, Moon, BookOpen, Utensils, Feather,
  Radio, Hammer, Wind, Plus, Minus, Info, ListChecks,
  Check, Lock, Play, ChevronDown
} from 'lucide-react';

// ─── Phase 7 P3: Status marker SVG shapes ────────────────────────────────────
function HollowCircleIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4.5" cy="4.5" r="3.2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}
function TriangleAlertIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 1.5L9 8.5H1L5 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
      <line x1="5" y1="4" x2="5" y2="6.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <circle cx="5" cy="7.3" r="0.55" fill="currentColor"/>
    </svg>
  );
}

const ICONS = {
  hydration: Droplets, deepwork: Flame, movement: Activity, sleep: Moon,
  reading: BookOpen, nutrition: Utensils, journal: Feather,
  outreach: Radio, build: Hammer, stillness: Wind
};

const CORE_OPTION_PILLARS = CORE_STREAK_OPTION_GROUPS.flat();

function addTaskEvent(task, type, extra = {}) {
  return {
    ...task,
    history: [...(task.history || []), { type, at: new Date().toISOString(), ...extra }],
  };
}

// GitHub-style grid constants
const CELL = 12;  // px per cell
const GAP  = 3;   // px gap between cells
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// Only show M / W / F labels (rows 0, 2, 4) like GitHub
const DOW_LABELS = ['M', '', 'W', '', 'F', '', ''];

// ─── 2026 grid data ───────────────────────────────────────────────────────────

function get2026Grid() {
  // Jan 1 2026 = Thursday → pad back to Monday Dec 29 2025
  const JAN1  = new Date(2026, 0,  1);
  const DEC31 = new Date(2026, 11, 31);

  const startDow = (JAN1.getDay()  + 6) % 7; // Mon=0
  const endDow   = (DEC31.getDay() + 6) % 7;

  const gridStart = new Date(JAN1);
  gridStart.setDate(gridStart.getDate() - startDow);   // Mon Dec 29 2025

  const gridEnd = new Date(DEC31);
  gridEnd.setDate(gridEnd.getDate() + (6 - endDow));   // Sun Jan  3 2027

  const cells = [];
  const d = new Date(gridStart);
  while (d <= gridEnd) {
    cells.push(localDateKey(d));
    d.setDate(d.getDate() + 1);
  }
  // 53 weeks × 7 = 371 cells
  return { cells, gridStart };
}

// Month label metadata: how many week-columns wide each month is
function buildMonthMeta(gridStart) {
  return MONTH_LABELS.map((label, mi) => {
    const first = new Date(2026, mi, 1);
    const next  = new Date(2026, mi + 1, 1); // first of next month (or Jan 2027)
    const startCol = Math.floor((first - gridStart) / (7 * 86400000));
    const endCol   = Math.floor((next  - gridStart) / (7 * 86400000));
    return { label, startCol, spanCols: endCol - startCol };
  });
}

// ─── Cell styling ─────────────────────────────────────────────────────────────

function cellStyle2026(ratio, isToday, isFuture, inYear) {
  // Padding cells outside 2026 — completely invisible
  if (!inYear) return { background: 'transparent', border: 'none', cursor: 'default' };

  // Future dates — barely visible placeholder
  if (isFuture) return {
    background: 'rgba(255,255,255,0.04)',
    borderColor: 'rgba(255,255,255,0.04)',
    cursor: 'default',
  };

  // Today with no data — blank but with engine-colour ring
  if (ratio === 0 && isToday) return {
    background: 'rgba(255,255,255,0.07)',
    boxShadow: '0 0 0 1.5px var(--el)',
    borderColor: 'transparent',
  };

  // Past with no data — plain blank (NOT locked/dimmed)
  if (ratio === 0) return {
    background: 'rgba(255,255,255,0.07)',
    borderColor: 'rgba(255,255,255,0.05)',
  };

  // Has data — engine colour, intensity scales with compliance ratio
  const pct = Math.round(20 + ratio * 68);
  return {
    background: `color-mix(in srgb, var(--el) ${pct}%, transparent)`,
    borderColor: isToday
      ? 'var(--el)'
      : `color-mix(in srgb, var(--el) ${Math.min(100, pct + 22)}%, transparent)`,
    boxShadow: isToday
      ? '0 0 0 1.5px var(--el), 0 0 8px color-mix(in srgb, var(--el) 45%, transparent)'
      : 'none',
  };
}

// ─── Year Heatmap ─────────────────────────────────────────────────────────────

function YearHeatmap({ protocol, today }) {
  const { cells, gridStart } = useMemo(get2026Grid, []);
  const monthMeta = useMemo(() => buildMonthMeta(gridStart), [gridStart]);
  const containerRef = useRef(null);

  // On mount, scroll so today is roughly centred
  useEffect(() => {
    if (!containerRef.current) return;
    const todayIdx = cells.indexOf(today);
    if (todayIdx < 0) return;
    const todayCol = Math.floor(todayIdx / 7);
    const scrollTarget = todayCol * (CELL + GAP) - containerRef.current.clientWidth / 2 + 60;
    containerRef.current.scrollLeft = Math.max(0, scrollTarget);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const DOW_W = CELL + 6; // width of DOW label column

  return (
    <div>
      {/* Month label row */}
      <div
        ref={containerRef}
        className="overflow-x-auto select-none"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div style={{ display: 'inline-block', paddingBottom: 8 }}>
          {/* Month labels */}
          <div className="flex" style={{ marginLeft: DOW_W + GAP, marginBottom: 4 }}>
            {monthMeta.map(({ label, spanCols }) => (
              <div
                key={label}
                className="font-data text-[9px] tracking-[0.14em] uppercase text-white/40 shrink-0"
                style={{ width: spanCols * (CELL + GAP) }}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Grid body: DOW labels + cells */}
          <div className="flex items-start" style={{ gap: GAP }}>
            {/* Day-of-week labels */}
            <div
              className="shrink-0"
              style={{
                display: 'grid',
                gridTemplateRows: `repeat(7, ${CELL}px)`,
                gap: GAP,
                width: DOW_W,
              }}
            >
              {DOW_LABELS.map((lbl, i) => (
                <div
                  key={i}
                  className="flex items-center justify-end pr-1 font-data text-[9px] text-white/30"
                  style={{ height: CELL }}
                >
                  {lbl}
                </div>
              ))}
            </div>

            {/* Cells: 7 rows, grid-flow-col */}
            <div
              style={{
                display: 'grid',
                gridTemplateRows: `repeat(7, ${CELL}px)`,
                gridAutoFlow: 'column',
                gridAutoColumns: CELL,
                gap: GAP,
              }}
            >
              {cells.map(iso => {
                const inYear  = iso >= '2026-01-01' && iso <= '2026-12-31';
                const isFuture = iso > today;
                const ratio   = inYear && !isFuture ? dayRatio(protocol, iso) : 0;
                const isToday = iso === today;
                const pillarsHit = inYear && !isFuture
                  ? Math.round(ratio * PILLARS.length)
                  : null;

                return (
                  <div
                    key={iso}
                    title={
                      !inYear   ? '' :
                      isFuture  ? iso :
                      pillarsHit !== null
                        ? `${iso}  ·  ${pillarsHit}/${PILLARS.length} pillars`
                        : iso
                    }
                    className="rounded-[2px] border transition-colors"
                    style={{
                      width: CELL,
                      height: CELL,
                      ...cellStyle2026(ratio, isToday, isFuture, inYear),
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-2 mt-3 font-data text-[9px] text-white/30" style={{ marginLeft: DOW_W + GAP }}>
            <span>LESS</span>
            {[0, 0.25, 0.5, 0.75, 1].map((s, i) => (
              <div
                key={i}
                className="rounded-[2px] border border-transparent"
                style={{
                  width: CELL, height: CELL,
                  background: s === 0
                    ? 'rgba(255,255,255,0.07)'
                    : `color-mix(in srgb, var(--el) ${Math.round(20 + s * 68)}%, transparent)`,
                }}
              />
            ))}
            <span>MORE</span>
            <span className="mx-1 text-white/15">·</span>
            <div className="rounded-[2px] border border-transparent" style={{ width: CELL, height: CELL, background: 'rgba(255,255,255,0.04)' }} />
            <span>FUTURE</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers (used by heatmap) ────────────────────────────────────────────────

function dayRatio(protocol, date) {
  const p = protocol[date] || {};
  return PILLARS.reduce((a, pl) => a + (p[pl.id] && p[pl.id] >= pl.target ? 1 : 0), 0) / PILLARS.length;
}

// ─── Micro-Tasks section ──────────────────────────────────────────────────────

// Phase 7 P3: monochrome meta — shape/border communicate state, not hue
const TASK_STATUS_META = {
  not_started:   { label: 'Not Started',   icon: null,              color: 'var(--status-muted)',   bg: 'rgba(255,255,255,0.02)', border: 'rgba(255,255,255,0.07)' },
  ongoing:       { label: 'Ongoing',       icon: HollowCircleIcon,  color: 'var(--status-ongoing)', bg: 'rgba(255,255,255,0.03)', border: 'rgba(160,160,160,0.22)' },
  high_priority: { label: 'High Priority', icon: TriangleAlertIcon, color: '#ffffff',               bg: 'rgba(255,191,0,0.07)',   border: 'rgba(255,191,0,0.45)' },
  overdue:       { label: 'Overdue',       icon: TriangleAlertIcon, color: '#ffffff',               bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.32)' },
};

// Auto-computed from startedAt timestamp — no manual overriding
function computeStatus(task) {
  if (!task.startedAt) return 'not_started';
  const days = (Date.now() - task.startedAt) / (1000 * 60 * 60 * 24);
  if (days < 30)  return 'ongoing';
  if (days < 90)  return 'high_priority';
  return 'overdue';
}

function MicroTasksSection() {
  const { state, updateGoal } = useArc();
  const [selectedGoalId, setSelectedGoalId] = useState('all');

  // Goals that have generated layers
  const goalsWithLayers = useMemo(
    () => (state.goals || []).filter(g => !g.deleted && !g.completed && g.layers && g.layers.length > 0),
    [state.goals]
  );

  const visibleGoals = selectedGoalId === 'all'
    ? goalsWithLayers
    : goalsWithLayers.filter(g => g.id === selectedGoalId);

  // Flat count for header
  const allTasks  = goalsWithLayers.flatMap(g => (g.layers || []).flatMap(l => l.tasks || []));
  const doneCount = allTasks.filter(t => t.done).length;
  const overdueCount = allTasks.filter(t => computeStatus(t) === 'overdue' && !t.done).length;

  // Helpers
  const startTask = (goalId, layerId, taskId) => updateGoal(goalId, g => ({
    ...g,
    layers: g.layers.map(l => l.id === layerId
      ? {
        ...l,
        tasks: l.tasks.map(t => t.id === taskId
          ? addTaskEvent({ ...t, startedAt: t.startedAt || Date.now() }, 'started')
          : t)
      }
      : l)
  }));

  const toggleDone = (goalId, layerId, taskId) => updateGoal(goalId, g => ({
    ...g,
    layers: g.layers.map(l => l.id === layerId
      ? {
        ...l,
        tasks: l.tasks.map(t => {
          if (t.id !== taskId) return t;
          const done = !t.done;
          return addTaskEvent({
            ...t,
            done,
            completedAt: done ? new Date().toISOString() : t.completedAt,
            reopenedAt: done ? t.reopenedAt : new Date().toISOString(),
          }, done ? 'completed' : 'reopened');
        })
      }
      : l)
  }));

  return (
    <div className="panel-strong p-5 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="rail-left font-head text-[15px] text-white/90">Micro-Tasks</div>
          <div className="font-data text-[11px] text-white/40 mt-0.5">
            {doneCount}/{allTasks.length} completed
            {overdueCount > 0 && (
              <span className="ml-2" style={{ color: 'var(--warning)' }}>· {overdueCount} overdue</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {goalsWithLayers.length > 1 && (
            <div className="relative">
              <select
                value={selectedGoalId}
                onChange={e => setSelectedGoalId(e.target.value)}
                className="input appearance-none pr-7 text-[11px] py-1.5 cursor-pointer"
                style={{ minWidth: 140 }}
              >
                <option value="all">All goals</option>
                {goalsWithLayers.map(g => (
                  <option key={g.id} value={g.id}>
                    {(g.prompt || g.topic || 'Goal').slice(0, 32)}
                  </option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          )}
          <Link to="/app/goals" className="btn text-[11px] px-3 py-1.5">+ New goal</Link>
        </div>
      </div>

      {allTasks.length === 0 ? (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center border border-white/8 bg-white/2">
            <ListChecks size={22} className="text-white/25" />
          </div>
          <div>
            <div className="font-head text-[16px] text-white/45">No tasks yet</div>
            <p className="mt-1 text-[12px] text-white/30 max-w-[300px] leading-[1.7]">
              Build a goal in Life Goals, generate the action plan, and tasks show up here as sequential levels.
            </p>
          </div>
          <Link to="/app/goals" className="btn px-5 py-2 text-[12px]">Go to Life Goals →</Link>
        </div>
      ) : visibleGoals.length === 0 ? (
        <div className="py-6 text-center text-[12px] text-white/30">No tasks for selected goal.</div>
      ) : (
        <div className="space-y-7 max-h-[560px] overflow-y-auto pr-1">
          {visibleGoals.map(goal => {
            // Sort layers by level (integer) or parse from name
            const sortedLayers = [...(goal.layers || [])].sort((a, b) => {
              const aL = a.level ?? parseInt(String(a.name || '').match(/\d/)?.[0] || '1', 10);
              const bL = b.level ?? parseInt(String(b.name || '').match(/\d/)?.[0] || '1', 10);
              return aL - bL;
            });

            // Compute per-layer unlock status: first layer always open; each next unlocks when prev all done
            const unlockedIds = new Set();
            sortedLayers.forEach((layer, idx) => {
              if (idx === 0) { unlockedIds.add(layer.id); return; }
              if ((sortedLayers[idx - 1].tasks || []).every(t => t.done)) unlockedIds.add(layer.id);
            });

            const goalLabel = (goal.prompt || goal.topic || 'Goal').slice(0, 60);

            return (
              <div key={goal.id}>
                {/* Goal label — only show in "all" view */}
                {selectedGoalId === 'all' && (
                  <div className="font-data text-[10px] uppercase tracking-[0.14em] text-white/35 mb-3 truncate" title={goalLabel}>
                    {goalLabel}
                  </div>
                )}

                <div className="space-y-4">
                  {sortedLayers.map((layer) => {
                    const unlocked = unlockedIds.has(layer.id);
                    const tasks    = layer.tasks || [];
                    const donePct  = tasks.length > 0 ? Math.round((tasks.filter(t => t.done).length / tasks.length) * 100) : 0;

                    return (
                      <div key={layer.id}>
                        {/* Level header */}
                        <div className="flex items-center gap-2 mb-2">
                          {unlocked ? (
                            <div className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--el)', boxShadow: '0 0 6px var(--el)' }} />
                          ) : (
                            <Lock size={10} className="text-white/30" />
                          )}
                          <span className="font-data text-[11px] tracking-[0.08em]"
                            style={{ color: unlocked ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.28)' }}>
                            {layer.name}
                          </span>
                          {unlocked && tasks.length > 0 && (
                            <span className="font-data text-[10px] text-white/30 ml-auto">{donePct}%</span>
                          )}
                          {!unlocked && (
                            <span className="font-data text-[10px] text-white/22 ml-auto">
                              Complete {sortedLayers[sortedLayers.indexOf(layer) - 1]?.name || 'previous level'} to unlock
                            </span>
                          )}
                        </div>

                        {/* Thin level progress track */}
                        {unlocked && tasks.length > 0 && (
                          <div className="mb-2 h-[2px] rounded-full bg-white/5 overflow-hidden" style={{ marginLeft: 12 }}>
                            <div className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${donePct}%`, background: 'var(--el)' }} />
                          </div>
                        )}

                        {/* Task rows */}
                        <div className="space-y-1.5 pl-3">
                          {tasks.map(task => {
                            if (!unlocked) {
                              // Locked preview
                              return (
                                <div key={task.id}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/4 bg-white/1 opacity-35">
                                  <div className="h-3.5 w-3.5 rounded-full border border-white/12 shrink-0" />
                                  <div className="text-[12px] text-white/35 flex-1 truncate">{task.text}</div>
                                  <Lock size={10} className="text-white/20 shrink-0" />
                                </div>
                              );
                            }

                            const status = computeStatus(task);
                            const m      = TASK_STATUS_META[status];
                            const SIcon  = m.icon;

                            return (
                              <div key={task.id}
                                className="flex items-start gap-3 px-3 py-3 rounded-lg border transition-colors"
                                style={{
                                  borderColor: task.done ? 'rgba(29,158,117,0.28)' : m.border,
                                  background:  task.done ? 'rgba(29,158,117,0.05)' : m.bg,
                                }}>
                                {/* Done toggle */}
                                <button
                                  onClick={() => toggleDone(goal.id, layer.id, task.id)}
                                  className="h-4 w-4 rounded-full shrink-0 mt-0.5 flex items-center justify-center transition-colors"
                                  style={{
                                    background: task.done ? '#1D9E75' : 'rgba(255,255,255,0.05)',
                                    border: `1px solid ${task.done ? '#1D9E75' : 'rgba(255,255,255,0.18)'}`,
                                  }}>
                                  {task.done && <Check size={8} className="text-[#050403]" strokeWidth={3} />}
                                </button>

                                {/* Text */}
                                <div className="flex-1 min-w-0">
                                  <div className="text-[13px] leading-[1.5]"
                                    style={{
                                      color: task.done ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.85)',
                                      textDecoration: task.done ? 'line-through' : 'none',
                                    }}>
                                    {task.text}
                                  </div>
                                </div>

                                {/* Start button or auto status badge */}
                                {!task.done && (
                                  task.startedAt ? (
                                    <div className="shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-full"
                                      style={{
                                        fontSize: 9, letterSpacing: '0.09em', padding: '3px 7px',
                                        color: m.color, background: m.bg, border: `1px solid ${m.border}`,
                                        fontFamily: "var(--font-ui)",
                                        textTransform: 'uppercase', fontWeight: 500, whiteSpace: 'nowrap',
                                      }}>
                                      {SIcon && <SIcon />}
                                      {m.label}
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => startTask(goal.id, layer.id, task.id)}
                                      className="shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-full transition-all hover:brightness-110"
                                      style={{
                                        fontSize: 9, letterSpacing: '0.09em', padding: '3px 9px',
                                        color: '#0B0A08', fontFamily: "var(--font-ui)",
                                        textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap',
                                        background: 'color-mix(in srgb, var(--el) 85%, transparent)',
                                        border: '1px solid var(--el)',
                                      }}>
                                      <Play size={8} strokeWidth={2.5} />
                                      Start
                                    </button>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ProtocolGrid() {
  const { state, mergeProtocol } = useArc();
  const today = useLocalDateKey();
  const todayP = state.protocol[today] || {};
  const setVal = (pid, v) => mergeProtocol(today, pid, Math.max(0, v));
  const coreStatus = getCoreStreakStatus(todayP);

  const activeDays = useMemo(() =>
    Object.keys(state.protocol).filter(d => dayRatio(state.protocol, d) > 0).length,
  [state.protocol]);

  return (
    <div className="fade-up space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-head text-[28px] md:text-[34px] text-white/95">Protocol Grid</h1>
        <p className="text-white/55 text-[13px] mt-1 max-w-[760px] leading-[1.7]">
          Ten pillars from the Life OS routine. Each completion is a small proof point. Repeated proof compounds into the trajectory you see on the dashboard.
        </p>
      </div>

      <div className="panel-strong p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="rail-left font-head text-[15px] text-white/90">Core Streak Protocol</div>
            <p className="mt-1 text-[12px] text-white/50 leading-[1.6] max-w-[720px]">
              A streak counts only when you log Hydration, Movement, Nutrition, Sleep, and either Reading or Stillness for the day. Past days lock after midnight.
            </p>
          </div>
          <div className="shrink-0 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
            <div className="eyebrow">Today</div>
            <div className="font-head text-[24px] leading-none mt-1" style={{ color: coreStatus.complete ? '#1D9E75' : 'var(--el)' }}>
              {coreStatus.completedSlots}/{coreStatus.totalSlots}
            </div>
          </div>
        </div>
      </div>

      {/* Today's pillar cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {PILLARS.map(p => {
          const Icon = ICONS[p.id] || Flame;
          const v = todayP[p.id] || 0;
          const isCore = CORE_STREAK_PILLARS.includes(p.id);
          const isCoreOption = CORE_OPTION_PILLARS.includes(p.id);
          return (
            <div
              key={p.id}
              className="panel p-4 group relative"
              style={isCore || isCoreOption ? {
                borderColor: `${p.accent}88`,
                boxShadow: `0 0 0 1px ${p.accent}22, 0 0 22px ${p.accent}14`,
              } : undefined}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: p.accent + '22', border: `1px solid ${p.accent}66` }}>
                    <Icon size={14} style={{ color: p.accent }} />
                  </div>
                  <div>
                    <div className="font-head text-[13px] text-white/85">{p.name}</div>
                    {(isCore || isCoreOption) && (
                      <div className="font-data text-[9px] uppercase tracking-[0.12em] mt-0.5" style={{ color: p.accent }}>
                        {isCore ? 'Core streak' : 'Core option'}
                      </div>
                    )}
                  </div>
                </div>
                <Tip text={p.hint} />
              </div>
              <div className="mt-2 font-data text-[11px] text-white/50">{v} / {p.target} {p.unit}</div>
              <PillarVisual pillar={p} value={v} />
              <div className="mt-3 flex items-center justify-between gap-2">
                <button className="btn px-2 py-2" onClick={() => setVal(p.id, v - 1)}><Minus size={12} /></button>
                <div className="flex-1 h-[3px] bg-white/6 rounded">
                  <div className="h-full rounded" style={{ width: `${Math.min(100, (v / p.target) * 100)}%`, background: p.accent }} />
                </div>
                <button className="btn px-2 py-2" onClick={() => setVal(p.id, v + 1)}><Plus size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* 2026 Compounding Grid */}
      <div className="panel-strong p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="rail-left font-head text-[15px] text-white/90">2026 Proof Grid</div>
            <div className="text-white/45 text-[12px] mt-1 leading-[1.6]">
              This grid is the receipt. It shows what you actually did, not what you planned to do.
            </div>
            <div className="font-data text-[11px] text-white/40 mt-0.5">
              {activeDays} day{activeDays !== 1 ? 's' : ''} logged · today is {today}
            </div>
          </div>
        </div>
        <YearHeatmap protocol={state.protocol} today={today} />
      </div>

      {/* Micro-Tasks — always visible below the grid */}
      <MicroTasksSection />
    </div>
  );
}

// ─── Tip ─────────────────────────────────────────────────────────────────────

function Tip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} className="text-white/40 hover:text-white/80" aria-label="info">
        <Info size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-30 w-[220px] p-3 rounded-md border bg-[#050403]/95 text-[12px] text-white/80" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {text}
        </div>
      )}
    </div>
  );
}

// ─── PillarVisual ─────────────────────────────────────────────────────────────

function PillarVisual({ pillar, value }) {
  const ratio = Math.min(1, value / pillar.target);
  switch (pillar.kind) {
    case 'bottle':
      return (
        <div className="mt-3 h-[64px] flex items-end justify-center gap-1">
          {Array.from({ length: pillar.target }).map((_, i) => (
            <div key={i} className="relative w-3.5 h-[54px] rounded-b-md rounded-t-sm overflow-hidden border border-white/10 bg-white/3">
              <div className="absolute left-0 right-0 bottom-0 transition-[height] duration-300" style={{ height: i < value ? '92%' : '0%', background: `linear-gradient(180deg, ${pillar.accent}cc, ${pillar.accent}77)` }} />
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-white/10" />
            </div>
          ))}
        </div>
      );
    case 'flame':
      return (
        <div className="mt-3 h-[64px] flex items-end justify-center gap-1">
          {Array.from({ length: pillar.target }).map((_, i) => {
            const on = i < value; const h = 18 + i * 9;
            return <div key={i} className="w-5 rounded-full transition-[height,opacity] duration-300" style={{ height: h, opacity: on ? 1 : 0.25, background: on ? `radial-gradient(circle at 50% 90%, ${pillar.accent}, ${pillar.accent}44 70%, transparent 80%)` : 'rgba(255,255,255,0.04)', filter: on ? `drop-shadow(0 0 10px ${pillar.accent}aa)` : 'none' }} />;
          })}
        </div>
      );
    case 'ring': {
      const size = 64; const r = 24; const c = 2 * Math.PI * r;
      return (
        <div className="mt-3 h-[64px] flex items-center justify-center">
          <svg width={size} height={size}>
            <circle cx={size/2} cy={size/2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
            <circle cx={size/2} cy={size/2} r={r} stroke={pillar.accent} strokeWidth="6" fill="none" strokeDasharray={c} strokeDashoffset={c - c*ratio} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 400ms ease' }} />
            <text x="50%" y="54%" textAnchor="middle" fontSize="11" fontFamily="Raleway" fontWeight="600" fill="#ffffff">{Math.round(ratio*100)}%</text>
          </svg>
        </div>
      );
    }
    case 'moon':
      return (
        <div className="mt-3 h-[64px] flex items-center justify-center">
          <div className="relative h-12 w-12 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)', boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.08), 0 0 20px ${pillar.accent}33` }}>
            <div className="absolute top-0 bottom-0 left-0" style={{ width: `${ratio*100}%`, background: `linear-gradient(90deg, ${pillar.accent}, ${pillar.accent}88)` }} />
          </div>
        </div>
      );
    case 'book':
      return (
        <div className="mt-3 h-[64px] flex items-end justify-center gap-[3px]">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="w-1.5 rounded-sm transition-[height] duration-300" style={{ height: 10 + (i < Math.round(ratio*10) ? 36 : 12), background: i < Math.round(ratio*10) ? pillar.accent : 'rgba(255,255,255,0.08)' }} />
          ))}
        </div>
      );
    case 'plate':
      return (
        <div className="mt-3 h-[64px] flex items-center justify-center gap-2">
          {Array.from({ length: pillar.target }).map((_, i) => (
            <div key={i} className="h-9 w-9 rounded-full border" style={{ borderColor: i < value ? pillar.accent : 'rgba(255,255,255,0.12)', background: i < value ? `radial-gradient(circle, ${pillar.accent}66, ${pillar.accent}11)` : 'rgba(255,255,255,0.03)' }} />
          ))}
        </div>
      );
    case 'quill':
      return <div className="mt-3 h-[64px] flex items-center justify-center font-data text-[11px]" style={{ color: value > 0 ? pillar.accent : 'rgba(255,255,255,0.35)' }}>{value > 0 ? 'SIGNED.' : 'UNWRITTEN'}</div>;
    case 'signal':
      return (
        <div className="mt-3 h-[64px] flex items-end justify-center gap-1">
          {Array.from({ length: pillar.target }).map((_, i) => (
            <div key={i} className="w-3.5 rounded-sm transition-[height] duration-300" style={{ height: 10 + i*8, background: i < value ? `linear-gradient(180deg, ${pillar.accent}, ${pillar.accent}55)` : 'rgba(255,255,255,0.06)' }} />
          ))}
        </div>
      );
    case 'anvil':
      return (
        <div className="mt-3 h-[64px] flex items-center justify-center gap-2">
          {Array.from({ length: pillar.target }).map((_, i) => (
            <div key={i} className="h-7 w-7 rounded-sm" style={{ background: i < value ? `linear-gradient(135deg, ${pillar.accent}aa, ${pillar.accent}55)` : 'rgba(255,255,255,0.05)', border: `1px solid ${i < value ? pillar.accent : 'rgba(255,255,255,0.08)'}`, transform: i < value ? 'rotate(-4deg)' : 'none' }} />
          ))}
        </div>
      );
    case 'breath': {
      const scale = 0.7 + ratio * 0.4;
      return (
        <div className="mt-3 h-[64px] flex items-center justify-center">
          <div className="rounded-full" style={{ width: 44, height: 44, transform: `scale(${scale})`, transition: 'transform 600ms ease', background: `radial-gradient(circle, ${pillar.accent}55, ${pillar.accent}11 60%, transparent 80%)`, border: `1px solid ${pillar.accent}66` }} />
        </div>
      );
    }
    default: return null;
  }
}
